import { createSlice } from "@reduxjs/toolkit";
import {
  type PostState,
  type Errors,
  VoteOption,
  ParticipantSettingState
} from "../../util/CreatePost/types"
import { initialApplicationErrors, initialVoteSettingErrors } from "../../util/CreatePost/utils"
import dayjs from "../../util/dayjs";
import { CreatePostType } from "../../util/CreatePost";
import { deleteHyphen, formatNumberHalfWidth } from "../../util/utils";

export const initialErrors: Errors = {
  title: "",
  category: "",
  group: "",
  details: "",
  tags: "",
  images: "",
  attachments: "",
  acceptVotes: "",
  acceptComments: "",
  agree: "",
  date: "",
  voteSetting: {
    voteQuestion: "",
    voteOptions: [],
    expiryDate: "",
    limitVoteOptions: "",
  },
  event: {
    startDate: "",
    endDate: "",
    description: "",
    venueName: "",
    postalCode: "",
    address: "",
    url: "",
    acceptApplication: "",
    applicationSetting: {
      startDate: "",
      endDate: "",
      sections: []
    },
    applicationCancelRule: ""
  }
}

const initialValues: PostState = {
  user: {},
  title: "",
  category: "",
  group: "",
  tags: [],
  details: "",
  images: [],
  attachments: [],
  acceptComments: false,
  acceptVotes: false,
  showNameOption: 1,
  isPrivate: false,
  privateUsers: [],
  voteSetting: {
    voteQuestion: "",
    voteOptions: [],
    expiryDate: undefined,
  },
  event: {
    startDate: undefined,
    endDate: undefined,
    description: "",
    venueName: "",
    postalCode: "",
    address: "",
    url: "",
    acceptApplication: false,
    applicationSetting: {
      startDate: undefined,
      endDate: undefined,
      sections: [],
    },
    applicationCancelRule: "",
  },
  changabled: {
    applicationSetting: true,
    voteSetting: true,
  }
}

type State = {
  createPostType: CreatePostType,
  values: PostState,
  errors: Errors,
}

const initialState: State = {
  createPostType: "UserPost",
  values: initialValues,
  errors: initialErrors
}

const createPostReducer = createSlice({
  name: "post",
  initialState,
  reducers: {
    reset: () => initialState,
    initialize: (state, action: {payload: {createPostType: CreatePostType}}) => {
      state.createPostType = action.payload.createPostType
      state.values = initialValues
      state.errors = initialErrors
    },
    setProps: (state, action) => {
      state.createPostType = setPostType(action.payload)
      state.values = formatState(action.payload)
      state.errors = initialEditPostErrors(action.payload)
    },
    setItem: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values = {
        ...state.values,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors = {
        ...state.errors,
        ...error
      };
    },
    setPrivateUsers: (state, action) => {
      state.values.privateUsers = action.payload.value;
    },
    setEventItem: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values.event = {
        ...state.values.event,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors.event = {
        ...state.errors.event,
        ...error
      };
    },
    setEventApplicationSetting: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values.event.applicationSetting = {
        ...state.values.event.applicationSetting,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors.event.applicationSetting = {
        ...state.errors.event.applicationSetting,
        ...error
      };
    },
    setEventApplicationSections: (state, action) => {
      const sections = action.payload.value
      state.values.event.applicationSetting.sections = sections;
      state.errors.event.applicationSetting =
        action.payload.error ?
          { ...state.errors.event.applicationSetting, sections: action.payload.error } :
          initialApplicationErrors(sections.filter((section: ParticipantSettingState) => section._destroy === 0).length)
    },
    setVoteSetting: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values.voteSetting = {
        ...state.values.voteSetting,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors.voteSetting = {
        ...state.errors.voteSetting,
        ...error
      };
    },
    setVoteOptions: (state, action) => {
      const options = action.payload.value
      state.values.voteSetting.voteOptions = options;
      state.errors.voteSetting =
        action.payload.error ?
          { ...state.errors.voteSetting, voteOptions: action.payload.error } :
          initialVoteSettingErrors(options.filter((option: VoteOption) => option._destroy === 0).length);
    },
    setErrors: (state, action) => {
      state.errors = {
        ...state.errors,
        ...action.payload
      }
    }
  }
});

export const {
  reset,
  initialize,
  setProps,
  setItem,
  setPrivateUsers,
  setEventItem,
  setEventApplicationSetting,
  setEventApplicationSections,
  setVoteSetting,
  setVoteOptions,
  setErrors,
} = createPostReducer.actions;

export default createPostReducer.reducer;

const formatState = (post: any) => {
  const voteOptions = post.vote_options

  const isVoted = voteOptions.some((vote: any) => vote.votes_count > 0)
  const voteOptionValues = voteOptions.map((option: any) => {
    return {
      id: option.id,
      optionText: option.option_text,
      isVoted: isVoted,
      key: crypto.randomUUID(),
      _destroy: 0,
    }
  })

  const postTags = post.post_tags.map((tag: any) => {
    return {
      id: tag.id,
      tag: tag.tag,
      _destroy: 0,
    }
  })

  const privateUsers = post.post_private_users.map((privateUser: any) => {
    return {
      id: privateUser.id, // post_private_user.id
      identifier: crypto.randomUUID(),
      userId: privateUser.privater_id,
      userType: privateUser?.privater_type,
      nickname: privateUser.nickname,
      image: privateUser?.image ?? "",
      _destroy: 0,
    }
  })

  const existsApplicantUser =
    post.post_events.length > 0 && post.post_events[0].users.length > 0

  const fetchVoteQuestion = () => {
    let voteQuestion;
    if (post.post_events.length > 0) {
      voteQuestion = post.post_events[0].voting_question
    } else {
      voteQuestion = post.vote_question
    }
    return voteQuestion || ""
  }

  const postValues: any = {
    id: post.id,
    creator: post?.creator,
    title: post.title,
    details: post.details,
    group: String(post.group.id),
    category: String(post.category.id),
    showNameOption: post.is_anonymous,
    acceptComments: post.enable_comments,
    acceptVotes: post.enable_participants,
    isPrivate: post.is_private,
    privateUsers: privateUsers,
    tags: postTags,
    images: post.images,
    attachments: post.attachments,
    voteSetting: {
      voteQuestion: fetchVoteQuestion(),
      expiryDate: post.expiry_date ?? undefined,
      voteOptions: voteOptionValues,
    },
    changabled: {
      applicationSetting: !existsApplicantUser,
      voteSetting: !isVoted,
    }
  }

  if (post.post_events.length > 0) {
    const postEvent = post.post_events[0]
    const settingValues: ParticipantSettingState[] = postEvent.participant_settings
      .map((setting: any) => {
        return {
          id: setting.id,
          name: setting.name,
          numberOfParticipants: setting.number_of_participants,
          feeAmount: setting.fee_amount,
          free: setting.free,
          _destroy: 0,
          key: crypto.randomUUID(),
        };
      });

    let zipcode = formatNumberHalfWidth(postEvent.postal_code);
    zipcode = deleteHyphen(zipcode);

    const postEventValues = {
      id: postEvent.id,
      startDate: dayjs(postEvent.start_date),
      endDate: dayjs(postEvent.end_date),
      description: postEvent.description,
      venueName: postEvent.venue_name,
      postalCode: zipcode,
      address: postEvent.address,
      url: postEvent.url,
      acceptApplication: settingValues.length > 0,
      applicationSetting: {
        startDate: dayjs(postEvent.application_start_date),
        endDate: dayjs(postEvent.application_end_date),
        sections: settingValues,
      },
      applicationCancelRule: postEvent.application_cancellation_rule,
    }
    postValues.event = postEventValues
  }
  return {...initialValues, ...postValues}
};

const initialEditPostErrors = (post: any) => {
  const errors: Errors = Object.assign({}, JSON.parse(JSON.stringify(initialErrors)));
  errors.event.applicationSetting = initialApplicationErrors(post.post_events[0]?.participant_settings.length)
  errors.voteSetting = initialVoteSettingErrors(post.vote_options.length)
  return errors
}

const setPostType = (post:any) => {
  if (!post.member_group) {
    return 'UserPost'
  }
  if (post.post_events.length > 0) {
    return 'GroupEventPost'
  }
  if (!post.is_private) {
    return 'GroupPublicPost'
  }
  return post.is_private_group_user ? 'GroupPrivateUserPost' : 'InsideGroupPost'
}